/**
 * @module AnalysisArtefactsTypes
 * @purpose Shared TypeScript types mirroring the backend artefacts_unified response shapes.
 * @depends backend/crates/evidenx-core/src/models/artefacts_unified.rs (camelCase serde output)
 * @dependedOnBy services/analysis-artefacts.ts, hooks/use-analysis-artefacts.ts, pages/case/analysis/*
 *
 * @rules
 * - Performance: types plus one pure per-row guard (readEventlogHost); no filtering or sorting
 * - State: n/a
 * - Errors: n/a
 */

export type ArtefactType =
  | 'mft'
  | 'prefetch'
  | 'shimcache'
  | 'pslist'
  | 'netstat'
  | 'eventlog'
  | 'file_inventory'
  | 'services'
  | 'users'
  | 'startup_items'

export type TimeBucketId = 'b0' | 'b1' | 'b2' | 'b3' | 'unknown'
export type IpRangeId = 'internal' | 'external' | 'unknown'

/**
 * Unified artefact row returned by GET /api/cases/:id/artefacts.
 * Mirrors UnifiedArtefactResponse in evidenx-core/src/models/artefacts_unified.rs.
 *
 * @behaviour All nullable backend fields use string | null (not undefined).
 * @errors n/a
 */
export interface AnalysisArtefact {
  artefactId: string
  artefactType: ArtefactType
  ingestionRunId: string
  evidenceNumberId: string
  endpointId: string
  displayName: string | null
  displayPath: string | null
  timestampPrimary: string | null
  sizeBytes: number | null
  md5: string | null
  sha1: string | null
  sha256: string | null
  extras: Record<string, unknown>
}

/**
 * Cursor-paginated artefact listing.
 * Mirrors ArtefactPageResponse in evidenx-core.
 *
 * @behaviour nextCursor is null when no further pages exist.
 * @errors n/a
 */
export interface AnalysisArtefactPage {
  items: AnalysisArtefact[]
  nextCursor: string | null
}

/**
 * Single entry within a filter facet category.
 * Mirrors FacetEntry in evidenx-core.
 *
 * @behaviour id is the filter value passed back to the query param; label is display text.
 * @errors n/a
 */
export interface FacetEntry {
  id: string
  label: string
  count: number
}

export type EndpointFacetEntry = FacetEntry

/**
 * All filter facets returned by GET /api/cases/:id/artefacts/facets.
 * Mirrors FacetsResponse in evidenx-core.
 *
 * @behaviour ipRanges is null when no netstat rows exist in the QC-gated set.
 * @errors n/a
 */
export interface AnalysisFacets {
  artefactTypes: FacetEntry[]
  endpoints: EndpointFacetEntry[]
  usernames: FacetEntry[]
  timeBuckets: FacetEntry[]
  ipRanges: FacetEntry[] | null
}

/**
 * Active filter state sent to both endpoints via repeated query params.
 *
 * @behaviour All fields optional; absent field means no filter applied for that category.
 * @errors n/a
 */
export interface AnalysisFilters {
  artefactType?: ArtefactType[]
  endpointId?: string[]
  username?: string[]
  timeBucket?: TimeBucketId[]
  ipRange?: IpRangeId[]
}

export type EventlogHostMatch = 'match' | 'mismatch' | 'unknown'

export interface EventlogHostInfo {
  endpointHostname: string
  computer: string | null
  computerMatch: EventlogHostMatch
}

const EVENTLOG_ARTEFACT_TYPE = 'eventlog'
const EVENTLOG_HOST_MATCHES: ReadonlySet<string> = new Set(['match', 'mismatch', 'unknown'])

/**
 * Reads the host fields the backend attaches to eventlog rows.
 *
 * @behaviour Returns null for non-eventlog rows or any malformed shape; absent computer reads as null.
 * @errors Never throws.
 */
export function readEventlogHost(
  artefactType: string,
  extras: Record<string, unknown>,
): EventlogHostInfo | null {
  if (artefactType !== EVENTLOG_ARTEFACT_TYPE) return null
  const { endpointHostname, computer, computerMatch } = extras
  if (typeof endpointHostname !== 'string') return null
  if (typeof computerMatch !== 'string' || !EVENTLOG_HOST_MATCHES.has(computerMatch)) return null
  if (computer !== undefined && computer !== null && typeof computer !== 'string') return null
  return {
    endpointHostname,
    computer: typeof computer === 'string' ? computer : null,
    computerMatch: computerMatch as EventlogHostMatch,
  }
}
